/**
 * Dashboard Scale Hook
 *
 * Calculates a zoom/scale factor for the dashboard based on viewport width
 * and TV mode settings. Ensures dashboard widgets are readable on large
 * displays (warehouse TVs, canteen screens, office monitors, etc.).
 *
 * Scale behavior:
 * - <= 1920px: 1.0 (no scaling)
 * - 2560px: ~1.15
 * - 3440px: ~1.35
 * - 3840px (4K): ~1.4
 * - TV mode adds its own scale multiplier on top
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import { useState, useEffect, useMemo } from 'react';
import { useTVMode } from '../contexts/TVModeContext';

const BASE_WIDTH = 1920;
const MAX_SCALE = 1.8;

/**
 * Calculate auto-scale factor based on viewport width.
 * Uses a gentle curve so scaling feels natural.
 */
function calcAutoScale(viewportWidth: number): number {
  if (viewportWidth <= BASE_WIDTH) return 1.0;

  // Proportional scaling: every 1000px extra width adds ~0.2 scale
  const extraWidth = viewportWidth - BASE_WIDTH;
  const scale = 1.0 + (extraWidth / 4500);

  return Math.min(scale, MAX_SCALE);
}

/**
 * Hook that provides a combined scale factor for the dashboard.
 * Automatically scales based on viewport width and TV mode settings.
 *
 * @returns {number} Combined scale factor (e.g., 1.0, 1.3, 1.5)
 */
export function useDashboardScale(): number {
  const tvMode = useTVMode();
  const tvScale = tvMode.state.config.enabled ? tvMode.state.config.scale : 1.0;

  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scale = useMemo(() => {
    const autoScale = calcAutoScale(viewportWidth);
    return autoScale * tvScale;
  }, [viewportWidth, tvScale]);

  return scale;
}
